// src/components/VerifyEmailPage.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import config from '../config/config';

const API_BASE_URL = config.apiUrl;

export default function VerifyEmail() {
  const { state } = useLocation();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const emailQueryParam = queryParams.get('email');

  const navigate = useNavigate();
  const [verified, setVerified] = useState(false);

  const saved = sessionStorage.getItem('pendingVerifyEmail');
  const initialEmail = state?.email || saved || emailQueryParam || '';

  const [email, setEmail] = useState(initialEmail);
  const [codes, setCodes] = useState(new Array(6).fill(''));
  const [resendLoading, setResendLoading] = useState(false);
  const [message, setMessage] = useState('');
  const inputsRef = useRef([]);

  // focus the first empty input on mount
useEffect(() => {
  if (!initialEmail) {
    setMessage('Missing email. Please go through signup or enter manually.');
  }
}, [initialEmail]);

useEffect(() => {
  if (!initialEmail ) return;

  (async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/auth/verify-status?email=${encodeURIComponent(initialEmail)}`
      );
      const { isVerified } = await res.json();
      if (isVerified) {
        setVerified(true);
        sessionStorage.setItem('isVerified', 'true');
        setMessage('✅ Email verified! Please click "Login" to continue.');
      }
      else {
        setVerified(false); // ❗ Explicitly set to false if not verified
      }
    } catch (err) {
      console.error('Status check failed', err);
      setVerified(false);
    }
  })();
}, [initialEmail, verified]);

  const handleCodeChange = (e, idx) => {
    const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 1);
    const newCodes = [...codes];
    newCodes[idx] = val;
    setCodes(newCodes);
    // move focus
    if (val && inputsRef.current[idx + 1]) {
      inputsRef.current[idx + 1].focus();
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const token = codes.join('');
    if (email === '' || token.length < 6) {
      setMessage('Please fill in your email and all 6 digits.');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token })
      });
      const data = await res.json();
      
      if (data.success) {
        // show toast
        setVerified(true);
        sessionStorage.setItem('isVerified', 'true');
          setMessage(data.message || 'Email verified successfully! You can now log in.');
      } else {
        setMessage(data.message || 'Verification failed.');
      }
    } catch (err) {
      setMessage('Something went wrong. Try again.');
    }
  };

  const handleResend = async e => {
    e.preventDefault();
    if (!email) {
      setMessage('Please enter your email first.');
      return;    
    }

    try {
      setResendLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/auth/resendVerification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();

      if (data.success) {
        setMessage(data.message || 'A new code has been sent!');
      } else {
        setMessage(data.message || 'Could not resend code.');
      }
    } catch (err) {
      console.error(err);
      setMessage('Network error. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };


  return (
    <div style={styles.containerWrap}>
      <div style={styles.container}>
        <h3 style={styles.title}>Verify your email address</h3>
        <h5 style={styles.subtitle}>
          We emailed you a six-digit code to 
          <input
  type="email"
  placeholder="email to be verified"
  value={email}
  onChange={e => setEmail(e.target.value)}
  disabled={verified}
  required
  style={{ 
    opacity: 0.9, 
    marginBottom: 16, 
    width: '100%',     // give it some width 
    padding: '8px' 
  }}
/>
Enter it below:
        </h5>

{!verified &&(        <form onSubmit={handleSubmit}>
          <div style={styles.codeSection}>
            {codes.map((c, idx) => (
              <input
                key={idx}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={c}
                onChange={e => handleCodeChange(e, idx)}
                ref={el => (inputsRef.current[idx] = el)}
                style={styles.codeBox}
              />
            ))}
          </div>

          <button
           type="submit"            
           disabled={verified}
           style={{
             ...styles.button,
             backgroundColor: verified ? '#ccc' : styles.button.background,
             cursor: verified ? 'not-allowed' : 'pointer',
             color: verified ? '#666' : styles.button.color,
           }}
         >
           {verified ? 'Verified' : 'Verify'}
         </button>
        </form>)}


        {message && <p style={styles.message}>{message}</p>}
        <p style={styles.resend}>
          Didn't get a code? 
          <button
          onClick={handleResend}
          disabled={resendLoading}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            textDecoration: 'underline',
            color: resendLoading ? '#999' : '#0066cc',
            cursor: resendLoading ? 'default' : 'pointer',
            fontSize: 'inherit'
          }}
        >
          {resendLoading ? 'Sending…' : 'Resend code'}
        </button>
        </p>
      </div>
      <button
          onClick={() => navigate('/login')}
          disabled={!verified}
          style={{
            width: '100%',
            padding: '10px',
            backgroundColor: verified ? '#0066cc' : '#ccc',
            color: '#fff',
            fontSize: '16px',
            border: 'none',
            borderRadius: 4,
            cursor: verified ? 'pointer' : 'not-allowed',
            marginTop: 16
          }}
        >
          Login
        </button>

      <div style={styles.verifySection}>
        <h3 style={styles.verifyTitle}>Need help?</h3>
        <p>If you didn't request a code, you can safely ignore this page.</p>
        <p>
          Questions? <a href="https://hawltechs.com/contact">We're here to help.</a>
        </p>
      </div>
    </div>
  );
}

const styles = {
  containerWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    fontFamily: "'Montserrat', sans-serif",
    margin: '40px',
  },
  container: {
    flex: 1,
    minWidth: 300,
    padding: 20,
    background: '#f5f5f5',
    borderRadius: 8,
  },
  title: { margin: '0 0 8px' },
  subtitle: { margin: '0 0 16px', lineHeight: 1.4 },
  emailInput: {
    width: '100%',
    padding: '8px',
    marginBottom: '16px',
    fontSize: '16px',
    borderRadius: 4,
    border: '1px solid #ccc',
  },
  codeSection: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  codeBox: {
    width: 40,
    height: 50,
    margin: '0 4px',
    fontSize: '24px',
    textAlign: 'center',
    borderRadius: 4,
    border: '1px solid #ccc',
    outline: 'none',
  },
  button: {
    display: 'block',
    width: '100%',
    padding: '10px',
    background: '#0066cc',
    color: '#fff',
    fontSize: '16px',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
  },
  message: { marginTop: '12px', textAlign: 'center' },
  resend: { marginTop: '8px', textAlign: 'center' },

  verifySection: {
    flex: 1,
    minWidth: 300,
    padding: 20,
    marginLeft: 20,
  },
  verifyTitle: { margin: '0 0 8px' },
};
